import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { useToaster } from '@flintmint/web-shared/hooks/use-toaster';
import {
  subscribeAgentRunEvents,
  subscribeAgentRunFinished,
  type AgentDocumentType,
  type AgentFinishResult,
  type AgentRunStoreEvent,
} from '@/hooks/llm/use-llm-agent';
import { isDefaultDocumentName } from '@/hooks/llm/use-llm-agent/document-utils';
import { CONTENT_SIZE_LIMIT_NOTIFICATION } from '@/extensions/content-size-limit';
import { useNavigateWithSearch } from '@/hooks/use-navigate-with-search';
import { hasUrlOverlay } from '@/hooks/use-url-overlay';

import { appRoutes } from '@/constants/routes';
import { urlOverlays } from '@/constants/url-overlays';
import { sidebarOverlayAtom } from '@/state';
import { getDocumentIdFromPathname } from '@/utils/helper-utils';

import type { SidebarOverlayData } from '@/constants/types';

const NETWORK_ERROR_NOTIFICATION = {
  type: 'error',
  title: 'Lost connection to the internet',
  description: 'Make sure your internet connection is restored and try again.',
  icon: 'wifi-error-02',
  iconMultiColor: true,
  toastOptions: { id: 'agent-network-error' },
} as const;

// Blocks have no route of their own; they open in the sidebar overlay instead.
type DocumentShortcut = { label: string; fallbackName: string; route?: string };

const documentShortcuts: Partial<Record<AgentDocumentType, DocumentShortcut>> = {
  notes: { label: 'Go to Note', fallbackName: 'the note', route: appRoutes.library },
  templates: { label: 'Go to Template', fallbackName: 'the template', route: appRoutes.library },
  userSources: { label: 'Go to Source', fallbackName: 'the source', route: appRoutes.cortex },
  blocks: { label: 'Go to Block', fallbackName: 'a block' },
};

// Every full-screen overlay's param, so an open overlay closes and the document is not left inert underneath it.
const urlOverlayParams = Object.values(urlOverlays).map(({ param }) => param);

const shortcutRemovedSearchParams = ['draftType', 'domain', ...urlOverlayParams];

const unknownDocumentShortcut: DocumentShortcut = {
  label: 'Go to Document',
  fallbackName: 'the document',
  route: appRoutes.library,
};

export const AgentRunNotifications = () => {
  const { showToastNotification } = useToaster();
  const navigateWithSearch = useNavigateWithSearch();
  const setSidebarOverlay = useSetAtom(sidebarOverlayAtom);
  const sidebarOverlay = useAtomValue(sidebarOverlayAtom);
  const { pathname, state: locationState } = useLocation();

  // Read at notification time rather than captured per subscription, so the open-overlay check
  // reflects the current sidebar without resubscribing on every overlay change.
  const sidebarOverlayRef = useRef(sidebarOverlay);
  useEffect(() => {
    sidebarOverlayRef.current = sidebarOverlay;
  }, [sidebarOverlay]);

  // Read at click time, so closing an overlay over the current document keeps the route state it has then.
  const locationStateRef = useRef(locationState);
  useEffect(() => {
    locationStateRef.current = locationState;
  }, [locationState]);

  // Finished runs whose toast is waiting on a pending document name. They outlive a resubscription of
  // the effect below, so they are tracked here rather than per subscription.
  const heldToastsRef = useRef(new Set<AgentFinishResult>());

  // A held toast is dropped as soon as its document comes on screen, even if the user leaves again.
  useEffect(() => {
    for (const result of heldToastsRef.current) {
      if (isDocumentAlreadyVisible(result, sidebarOverlay)) heldToastsRef.current.delete(result);
    }
  }, [pathname, sidebarOverlay]);

  useEffect(() => {
    const heldToasts = heldToastsRef.current;

    return () => heldToasts.clear();
  }, []);

  useEffect(() => {
    return subscribeAgentRunEvents(({ event }) => {
      const notification = getAgentErrorNotification(event);
      if (notification) showToastNotification(notification);
    });
  }, [showToastNotification]);

  useEffect(() => {
    return subscribeAgentRunFinished((result, pendingDocumentName) => {
      // Whether to notify is decided at completion; a pending name only delays showing the toast.
      if (!result.hasEdit || !result.requestId) return;
      if (isDocumentAlreadyVisible(result, sidebarOverlayRef.current)) return;

      const shortcut = (result.documentType && documentShortcuts[result.documentType]) || unknownDocumentShortcut;

      const showReadyToast = (documentName: string | undefined) => {
        showToastNotification({
          type: 'success',
          title: 'Ready to view',
          description: resolveToastDescription(documentName) ?? shortcut.fallbackName,
          action: {
            label: shortcut.label,
            onClick: () => {
              if (shortcut.route) {
                navigateWithSearch(`${shortcut.route}/${result.requestId}`, {
                  removeSearchParams: shortcutRemovedSearchParams,
                });
                return;
              }

              const { pathname: currentPathname, search, hash } = window.location;
              const searchParams = new URLSearchParams(search);

              // A block overlay needs a document route underneath it to open over.
              if (!getDocumentIdFromPathname(currentPathname)) {
                navigateWithSearch(appRoutes.library, {
                  removeSearchParams: shortcutRemovedSearchParams,
                });
              } else if (Object.values(urlOverlays).some((overlay) => hasUrlOverlay(searchParams, overlay))) {
                // Staying on the current document keeps its draft params, hash and route state.
                navigateWithSearch(`${currentPathname}${hash}`, {
                  replace: true,
                  state: locationStateRef.current,
                  removeSearchParams: urlOverlayParams,
                });
              }

              setSidebarOverlay({
                type: 'blocks',
                blockId: result.requestId,
                context: {
                  skipOriginalBlockWarning: true,
                },
              });
            },
          },
        });
      };

      if (!pendingDocumentName) {
        showReadyToast(result.documentName);
        return;
      }

      const heldToasts = heldToastsRef.current;
      heldToasts.add(result);

      void pendingDocumentName.then((documentName) => {
        // Dropped while the name was pending: the document came on screen or the notifications unmounted.
        // The visibility check covers a document opened so recently that the tracking effect has not run yet.
        if (!heldToasts.delete(result) || isDocumentAlreadyVisible(result, sidebarOverlayRef.current)) return;

        showReadyToast(documentName);
      });
    });
  }, [showToastNotification, navigateWithSearch, setSidebarOverlay]);

  return null;
};

function resolveToastDescription(documentName: string | undefined): string | undefined {
  const trimmed = documentName?.trim();
  if (!trimmed || isDefaultDocumentName(trimmed)) return undefined;

  return trimmed;
}

// The toast only helps when the finished document is somewhere the user is not already looking.
function isDocumentAlreadyVisible(result: AgentFinishResult, sidebarOverlay: SidebarOverlayData | null): boolean {
  if (result.documentType === 'blocks') {
    // Draft blocks open under `draftId`; saved blocks under `blockId`. The run's requestId matches whichever is set.
    return sidebarOverlay?.type === 'blocks' && (sidebarOverlay.blockId ?? sidebarOverlay.draftId) === result.requestId;
  }

  return getDocumentIdFromPathname(window.location.pathname) === result.requestId;
}

function getAgentErrorNotification(event: AgentRunStoreEvent['event']) {
  if (event.type !== 'errored') return undefined;
  if (event.reason === 'content_too_large') return CONTENT_SIZE_LIMIT_NOTIFICATION;
  if (event.network) return NETWORK_ERROR_NOTIFICATION;
  return undefined;
}
